export interface AnnotationMeta {
  iconKey: "up" | "down" | "handoff" | "text";
  tone: "ok" | "err" | "warn" | "info";
  label: string;
  /** One-line explanation of what this annotation kind means (shown on hover). */
  tip: string;
  snippet: string;
  focusKey: string | null;
}

/**
 * The `annotations` table is overloaded: it stores human feedback (赞/踩/转人工)
 * AND internal system bookkeeping (cache_*, block_state). This glossary gives
 * every kind a human label + a one-line explanation, so the console never shows
 * a raw machine string like "cache_policy" with no context. Source of truth for
 * the labels is openspec/review-question-from-conversation.md §3.
 */
const RATING_TIP = "用户对回答的反馈（赞 / 踩）——主动点击，或由用户的后续消息推断";
const HANDOFF_META = { label: "转人工", tip: "对话被转接给人工客服——一处需要补足的知识缺口" };

export const KIND_META: Record<string, { label: string; tip: string }> = {
  rating: { label: "评价", tip: RATING_TIP },
  handoff: HANDOFF_META,
  cache_policy: { label: "缓存评估", tip: "系统评估这条回答能否被缓存 / 沉淀，记录波动性与 promote / demote 阈值" },
  cache_served: { label: "缓存命中", tip: "本轮回答由缓存（L2 / L3）直接给出，未重新生成" },
  cache_promoted: { label: "沉淀入库", tip: "这条回答被沉淀进可复用知识库（learned knowledge）" },
  block_state: { label: "步骤进度", tip: "用户在解决方案步骤上的进度被保存" },
  feedback: { label: "反馈", tip: "用户对回答的反馈" }
};

function asRecord(v: unknown): Record<string, unknown> {
  return typeof v === "object" && v !== null ? (v as Record<string, unknown>) : {};
}
function str(v: unknown): string { return typeof v === "string" ? v : ""; }

export function annotationMeta(a: { kind: string; payload: unknown; responseId: string | null }): AnnotationMeta {
  const p = asRecord(a.payload);
  const turnId = str(p.turnId) || null;
  // Prefer responseId — it matches the persisted assistant turn's `responseId`
  // (`chatcmpl-…`). `payload.turnId` (`a_…`) is a client-side id that does NOT
  // match any persisted turn id, so it's only a last-resort fallback.
  const focusKey = a.responseId ?? turnId ?? null;
  const reason = str(p.reason);
  const text = str(p.text);
  // Implicit (NL-inferred) ratings are noisier than an explicit click — mark them.
  const implicit = p.source === "nl" || p.implicit === true;

  if (a.kind === "rating") {
    const up = p.rating === "up";
    // The question is no longer copied onto the payload — it lives on the
    // conversation turn (the row deep-links there). Snippet = reason, or the bare
    // verdict when no reason was given.
    const snippet = reason || (up ? "赞" : "踩");
    const suffix = implicit ? "·隐式" : "";
    const tip = implicit
      ? "由用户的后续消息推断得出的评价（非主动点击），可能有误判"
      : RATING_TIP;
    return up
      ? { iconKey: "up", tone: "ok", label: `赞${suffix}`, tip, snippet, focusKey }
      : { iconKey: "down", tone: "err", label: `踩${suffix}`, tip, snippet, focusKey };
  }
  if (a.kind === "handoff") {
    return { iconKey: "handoff", tone: "warn", label: HANDOFF_META.label, tip: HANDOFF_META.tip, snippet: text || "转人工", focusKey };
  }
  const meta = KIND_META[a.kind];
  const label = meta?.label ?? a.kind;
  return {
    iconKey: "text",
    tone: "info",
    label,
    tip: meta?.tip ?? "系统事件",
    snippet: reason || text || label,
    focusKey
  };
}
